import { useState, useEffect, useMemo, useRef } from 'react'
import { Loader2, Check, X, AlertTriangle } from 'lucide-react'
import { updateApp, checkAppName, listCategories, type App, type NameCheckResult, type Category } from '../lib/api'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => any>(fn: T, ms: number) {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), ms)
  }
}

function sanitizeName(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50)
}

interface EditAppInfoModalProps {
  app: App
  onClose: () => void
  onSaved: (updatedApp: App) => void
}

export default function EditAppInfoModal({ app, onClose, onSaved }: EditAppInfoModalProps) {
  const [displayName, setDisplayName] = useState(app.display_name || '')
  const [description, setDescription] = useState(app.description || '')
  const [category, setCategory] = useState(app.category || '')
  const [appName, setAppName] = useState(app.name)
  const [nameCheck, setNameCheck] = useState<NameCheckResult | null>(null)
  const [checkingName, setCheckingName] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [categories, setCategories] = useState<Category[]>([])

  const currentRequestRef = useRef<number>(0)

  useEffect(() => {
    listCategories().then(setCategories).catch(() => {})
  }, [])

  const nameChanged = appName !== app.name
  const isStopped = app.status === 'stopped'

  const debouncedCheckName = useMemo(
    () => debounce(async (value: string) => {
      if (value.length < 3) {
        setNameCheck(null)
        return
      }

      const requestId = ++currentRequestRef.current
      setCheckingName(true)
      try {
        const result = await checkAppName(value, app.name)
        if (requestId === currentRequestRef.current) {
          setNameCheck(result)
        }
      } catch {
        if (requestId === currentRequestRef.current) {
          setNameCheck(null)
        }
      } finally {
        if (requestId === currentRequestRef.current) {
          setCheckingName(false)
        }
      }
    }, 300),
    [app.name]
  )

  useEffect(() => {
    if (nameChanged && appName.length >= 3) {
      debouncedCheckName(appName)
    } else {
      setNameCheck(null)
      setCheckingName(false)
    }
  }, [appName, nameChanged, debouncedCheckName])

  const isNameValid = !nameChanged || (appName.length >= 3 && nameCheck?.available === true)
  const hasChanges =
    displayName !== (app.display_name || '') ||
    description !== (app.description || '') ||
    category !== (app.category || '') ||
    nameChanged

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!hasChanges || (nameChanged && !isNameValid)) return

    setSaving(true)
    setError(null)

    try {
      const updates: Record<string, string | null> = {}
      if (displayName !== (app.display_name || '')) {
        updates.display_name = displayName || null
      }
      if (description !== (app.description || '')) {
        updates.description = description || null
      }
      if (category !== (app.category || '')) {
        updates.category = category || null
      }
      if (nameChanged) {
        updates.name = appName
      }

      const updated = await updateApp(app.name, updates)
      onSaved(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update app')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Edit App Info</h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-5">
            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="My Awesome App"
                maxLength={60}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The name shown in the dashboard. Leave empty to use the URL slug.
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does your app do?"
                rows={3}
                maxLength={2000}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">No category</option>
                {categories.map(cat => (
                  <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Helps others discover your app in Browse Apps.
              </p>
            </div>

            {/* URL Slug */}
            <div>
              <label className="block text-sm font-medium mb-1.5">URL Slug</label>
              <div className="relative">
                <input
                  type="text"
                  value={appName}
                  onChange={(e) => setAppName(sanitizeName(e.target.value))}
                  disabled={!isStopped}
                  className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {nameChanged && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingName ? (
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    ) : nameCheck?.available ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : nameCheck && !nameCheck.available ? (
                      <X className="w-4 h-4 text-destructive" />
                    ) : null}
                  </div>
                )}
              </div>
              {!isStopped ? (
                <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Stop the app to change its URL slug.
                </p>
              ) : nameChanged ? (
                <div className="mt-1.5 space-y-1">
                  {nameCheck?.available === false ? (
                    <p className="text-xs text-destructive">
                      Name &quot;{appName}&quot; is taken.
                      {nameCheck.suggestion && ` Try "${nameCheck.suggestion}".`}
                    </p>
                  ) : appName.length >= 3 ? (
                    <p className="text-xs text-muted-foreground">
                      URL will change to <code className="bg-secondary px-1 rounded">/a/{appName}/</code>
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Enter at least 3 characters
                    </p>
                  )}
                  <p className="text-xs text-amber-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    Existing links to <code className="bg-secondary px-1 rounded">/a/{app.name}/</code> will stop working.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-1.5">
                  Currently at <code className="bg-secondary px-1 rounded">/a/{app.name}/</code>
                </p>
              )}
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !hasChanges || (nameChanged && !isNameValid)}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
